import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { CalendarClock, Plus, Wand2, History, User, XCircle, Users, AlertTriangle, ArrowLeftRight } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { fmtDateTime, useAppTz } from "@/lib/tz";

function toLocalInputValue(iso) {
  if (!iso) return "";
  try {
    // Convert stored UTC → "YYYY-MM-DDTHH:mm" in the browser's local zone so the
    // datetime-local input is prefilled sensibly.
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function fromLocalInputValue(v) {
  if (!v) return "";
  try {
    // Treat the value as local wall-clock, convert to UTC ISO.
    return new Date(v).toISOString();
  } catch { return v; }
}

const statusBadge = (s) => {
  const map = {
    active: { text: "Active now", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30" },
    scheduled: { text: "Scheduled", cls: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30" },
    ended: { text: "Ended", cls: "bg-muted text-muted-foreground border-border" },
    cancelled: { text: "Cancelled", cls: "bg-destructive/10 text-destructive border-destructive/30 line-through decoration-destructive/60" },
  };
  const m = map[s] || map.ended;
  return <Badge className={`${m.cls} border`} variant="outline">{m.text}</Badge>;
};

export default function CabinArrangementSection({ charter, cabins, onChanged }) {
  const { admin } = useAuth();
  const canEdit = ["developer", "admin", "manager", "supervisor"].includes(admin?.role);
  const tz = useAppTz();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("new"); // "new" | "correct" | "edit"
  const [source, setSource] = useState(null); // arrangement being corrected/edited
  const [form, setForm] = useState({ effective_from: "", notes: "", guests: [] });
  // Focus cabin id — set by the "Move existing guest here" flow from the
  // Cabins page. The dialog highlights this cabin's rows in the roster so
  // the operator sees where the incoming guest should land.
  const [focusCabinId, setFocusCabinId] = useState(null);
  const [focusCabinName, setFocusCabinName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/charters/${charter.id}/arrangements`);
      setItems(data || []);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Timeline load failed"); }
    finally { setLoading(false); }
  }, [charter.id]);

  useEffect(() => { load(); }, [load]);

  const activeRow = useMemo(() => items.find((r) => r.status === "active"), [items]);

  // Listen for the "schedule a move to this cabin" intent dispatched by the
  // Cabins-page Free-berth chooser. When our charter matches, we open the
  // New Arrangement dialog automatically and stash `focus_cabin_id` so the
  // roster highlights the target cabin.
  useEffect(() => {
    const onOpen = (ev) => {
      const d = ev?.detail;
      if (!d || d.charter_id !== charter.id) return;
      setFocusCabinId(d.focus_cabin_id || null);
      setFocusCabinName(d.focus_cabin_name || "");
      openNew();
    };
    window.addEventListener("yspa:open-arrangement", onOpen);
    return () => window.removeEventListener("yspa:open-arrangement", onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charter.id]);

  // Complete cabin roster from the active arrangement — includes EVERY cabin
  // on the yacht (even those without a guest) so the operator can never
  // "miss" a cabin when reviewing the current fleet layout. Empty cabins
  // surface as "— no guest —" so it's obvious they're vacant, not hidden.
  const activeCabinRoster = useMemo(() => {
    const byCabin = new Map();
    (cabins || []).forEach((c) => byCabin.set(c.id, {
      cabin_id: c.id,
      cabin_name: c.name,
      occupancy_status: c.occupancy_status || "guest",
      guests: [],
    }));
    ((activeRow?.guests) || []).forEach((g) => {
      if (!g.cabin_id) return;
      const entry = byCabin.get(g.cabin_id);
      if (entry) entry.guests.push(g);
    });
    return Array.from(byCabin.values());
  }, [cabins, activeRow]);

  // Live preview of the roster currently in the edit form. Surfaces cabin
  // occupancy at a glance so managers spot an accidentally-empty roster
  // before saving — the backend guardrail rejects empty saves too, but this
  // gives them the "aha" moment before they click Save.
  const rosterPreview = useMemo(() => {
    const guests = form.guests || [];
    if (guests.length === 0) return { count: 0, cabins: [] };
    const byCabin = new Map();
    for (const g of guests) {
      const key = g.cabin_id || "_none";
      const label = g.cabin_name || (cabins.find((c) => c.id === g.cabin_id) || {}).name || "—";
      const entry = byCabin.get(key) || { name: label, n: 0 };
      entry.n += 1;
      byCabin.set(key, entry);
    }
    return {
      count: guests.length,
      cabins: Array.from(byCabin.values()).sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [form.guests, cabins]);

  // Would saving this roster be blocked by the backend guardrail? Only when
  // the charter has other populated arrangements.
  const willTripEmptyRosterGuard = useMemo(() => {
    if ((form.guests || []).length > 0) return false;
    return items.some((r) => !r.cancelled_at && (r.guests || []).length > 0);
  }, [form.guests, items]);

  const openNew = () => {
    setMode("new");
    setSource(null);
    // Build the roster from the LIVE charter guests — that is the source of
    // truth in Trips & guests. The previous implementation started from the
    // active arrangement snapshot, so any guest added AFTER that snapshot
    // was recorded (e.g. Miss Noor, James Jnr) never appeared in the New
    // Arrangement dialog and couldn't be moved to another cabin without
    // manually recreating the entry.
    //
    // If the active arrangement disagrees with the live cabin_id on any
    // guest (rare, but possible after direct Trips edits), we prefer the
    // arrangement's value so the dialog shows the "current version" the
    // manager approved most recently. The manager can override it with
    // the per-guest Select before saving.
    const arrCabinById = new Map(
      ((activeRow?.guests) || []).map((g) => [g.guest_id || g.id, g.cabin_id])
    );
    const base = (charter.guests || []).map((g) => {
      const gid = g.guest_id || g.id;
      const cabin_id = arrCabinById.get(gid) || g.cabin_id;
      return {
        guest_id: gid,
        name: g.name,
        female: !!g.female,
        cabin_id,
        cabin_name: (cabins.find((c) => c.id === cabin_id) || {}).name || g.cabin_name || "",
        pin: g.pin || "",
        partner_guest_id: g.partner_guest_id || null,
      };
    });
    setForm({ effective_from: "", notes: "", guests: base });
    setOpen(true);
  };

  const openCorrect = (row) => {
    setMode("correct");
    setSource(row);
    setForm({ effective_from: row.effective_from, notes: "", guests: (row.guests || []).map((g) => ({ ...g })) });
    setOpen(true);
  };

  const openEdit = (row) => {
    setMode("edit");
    setSource(row);
    setForm({ effective_from: row.effective_from, notes: row.notes || "", guests: (row.guests || []).map((g) => ({ ...g })) });
    setOpen(true);
  };

  const setGuestCabin = (guestId, cabinId) => {
    const cab = cabins.find((c) => c.id === cabinId);
    setForm((f) => ({
      ...f,
      guests: f.guests.map((g) => g.guest_id === guestId ? { ...g, cabin_id: cabinId, cabin_name: cab?.name || g.cabin_name } : g),
    }));
  };

  const submit = async () => {
    if (!form.effective_from) { toast.error("Effective-from datetime is required"); return; }
    const payload = {
      effective_from: fromLocalInputValue(form.effective_from),
      notes: form.notes || "",
      guests: form.guests || [],
    };
    try {
      if (mode === "new") {
        await api.post(`/admin/charters/${charter.id}/arrangements`, payload);
        toast.success("Arrangement scheduled");
      } else if (mode === "correct") {
        await api.post(`/admin/charters/${charter.id}/arrangements/${source.id}/correct`, payload);
        toast.success("Arrangement corrected (original moved to audit)");
      } else if (mode === "edit") {
        await api.put(`/admin/charters/${charter.id}/arrangements/${source.id}`, payload);
        toast.success("Scheduled arrangement updated");
      }
      setOpen(false);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Save failed"); }
  };

  const cancelArrangement = async (row) => {
    const when = fmtDateTime(row.effective_from);
    const cabinsLabel = Array.from(new Set((row.guests || []).map((g) => g.cabin_name).filter(Boolean))).join(", ") || "no cabin assigned";
    const guestsLabel = (row.guests || []).map((g) => `${g.name} → ${g.cabin_name || "?"}`).join("\n  · ") || "no guests";
    const confirmMsg =
      `Cancel this scheduled cabin arrangement?\n\n` +
      `Effective: ${when}\n` +
      `Cabins: ${cabinsLabel}\n` +
      `Guests:\n  · ${guestsLabel}\n\n` +
      `The arrangement will be marked Cancelled — the current active roster is preserved and any reserved slot is released.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.post(`/admin/charters/${charter.id}/arrangements/${row.id}/cancel`);
      toast.success("Arrangement cancelled");
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Cancel failed"); }
  };

  return (
    <div className="border-t border-border p-5 bg-muted/10" data-testid={`arrangement-section-${charter.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-accent" />
          <div className="overline text-primary/60">Cabin arrangement timeline</div>
          <span className="text-xs text-muted-foreground">· all times shown in <span className="font-mono">{tz}</span></span>
        </div>
        {canEdit && (
          <Button size="sm" variant="outline" onClick={openNew} data-testid={`new-arrangement-${charter.id}`}>
            <Plus className="h-3.5 w-3.5 mr-2" /> New arrangement
          </Button>
        )}
      </div>

      {/* Fleet snapshot — every cabin on the yacht, mapped to the guests
          currently living in it (based on the active arrangement). Ensures
          crew cabins & vacant cabins never disappear from view. */}
      {cabins.length > 0 && (
        <div className="mb-4 border border-border bg-background/60 p-3" data-testid={`arrangement-fleet-${charter.id}`}>
          <div className="text-[11px] uppercase tracking-widest text-primary/60 mb-2">
            Current fleet · {cabins.length} cabin{cabins.length === 1 ? "" : "s"}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {activeCabinRoster.map((entry) => {
              const isLocked = ["crew", "not_available"].includes(entry.occupancy_status);
              return (
                <div
                  key={entry.cabin_id}
                  className={`flex items-start justify-between gap-2 px-3 py-2 border text-sm ${
                    isLocked ? "border-amber-500/40 bg-amber-500/5" :
                    entry.guests.length === 0 ? "border-border/70 bg-muted/30" :
                    "border-emerald-500/30 bg-emerald-500/5"
                  }`}
                  data-testid={`arrangement-fleet-cabin-${entry.cabin_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{entry.cabin_name}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {entry.guests.length === 0
                        ? (isLocked ? (entry.occupancy_status === "crew" ? "Crew use" : "Not available") : "— no guest —")
                        : entry.guests.map((g) => g.name).join(" · ")}
                    </div>
                  </div>
                  {entry.guests.length > 0 && (
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      {entry.guests.length}
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading timeline…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground">No arrangements yet. The initial charter roster is created automatically on first save.</div>
      ) : (
        <ol className="space-y-3" data-testid={`arrangement-list-${charter.id}`}>
          {items.map((row) => {
            const isCorrection = !!row.corrects_arrangement_id;
            return (
              <li key={row.id} className={`border ${row.status === "active" ? "border-emerald-500/50 bg-emerald-500/5" : row.status === "cancelled" ? "border-destructive/40 bg-destructive/5 opacity-70" : "border-border bg-card"} p-4`} data-testid={`arrangement-${row.id}`}>
                <div className="flex flex-wrap items-center gap-3 justify-between">
                  <div className="flex items-center gap-3">
                    <CalendarClock className={`h-4 w-4 ${row.status === "cancelled" ? "text-destructive" : "text-primary"}`} />
                    <div>
                      <div className={`font-medium ${row.status === "cancelled" ? "line-through decoration-destructive/50" : ""}`}>{fmtDateTime(row.effective_from)}</div>
                      <div className="text-xs text-muted-foreground">
                        v{row.version}{isCorrection && <span className="ml-1 text-accent">· correction</span>}
                        {row.effective_to && <> · until {fmtDateTime(row.effective_to)}</>}
                        {row.created_by_email && <> · by {row.created_by_email}</>}
                        {row.cancelled_at && <span className="ml-2 text-destructive">· cancelled {fmtDateTime(row.cancelled_at)}{row.cancelled_by_email ? ` by ${row.cancelled_by_email}` : ""}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {statusBadge(row.status)}
                    {canEdit && row.status === "scheduled" && (
                      <Button size="sm" variant="outline" onClick={() => openEdit(row)} data-testid={`edit-arrangement-${row.id}`}>Edit</Button>
                    )}
                    {canEdit && row.status === "scheduled" && (
                      <Button size="sm" variant="outline" onClick={() => cancelArrangement(row)} className="text-destructive border-destructive/40" data-testid={`cancel-arrangement-${row.id}`}>
                        <XCircle className="h-3 w-3 mr-1" /> Cancel
                      </Button>
                    )}
                    {canEdit && row.status === "ended" && (
                      <Button size="sm" variant="outline" onClick={() => openCorrect(row)} className="border-accent/40 text-accent" data-testid={`correct-arrangement-${row.id}`}>
                        <Wand2 className="h-3 w-3 mr-1" /> Correct
                      </Button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-3">
                  {(row.guests || []).map((g) => (
                    <div key={g.guest_id} className="flex items-center gap-2 text-sm border border-border/60 bg-background/60 px-3 py-2">
                      <User className="h-3 w-3 text-muted-foreground" />
                      <span className="font-medium">{g.name}</span>
                      <span className="text-muted-foreground text-xs">→ {g.cabin_name}</span>
                    </div>
                  ))}
                  {(row.guests || []).length === 0 && (
                    <div className="text-xs text-muted-foreground col-span-full">Empty roster.</div>
                  )}
                </div>
                {row.notes && <div className="text-xs text-muted-foreground mt-2">Note: {row.notes}</div>}
              </li>
            );
          })}
        </ol>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setFocusCabinId(null); setFocusCabinName(""); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`arrangement-dialog-${charter.id}`}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {mode === "new" && "New cabin arrangement"}
              {mode === "correct" && "Correct arrangement (audited replacement)"}
              {mode === "edit" && "Edit scheduled arrangement"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* When invoked via the Cabins-page "Move existing guest here"
                flow, surface the target cabin front-and-centre so the
                manager immediately understands the intent. Roster rows
                below get a highlighted state whenever they're currently
                assigned to the target cabin (or picked up by it). */}
            {focusCabinId && (
              <div
                className="flex items-start gap-3 border border-accent/50 bg-accent/10 p-3 text-sm text-accent-foreground"
                data-testid="af-focus-hint"
              >
                <ArrowLeftRight className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium">Move a guest into {focusCabinName || "the selected cabin"}</div>
                  <div className="text-[11px] mt-0.5 opacity-80">Pick any guest below and switch their cabin dropdown to <span className="font-medium">{focusCabinName}</span>. Save to schedule the move.</div>
                </div>
              </div>
            )}
            {/* Roster preview — surfaces guest count + cabins the moment the
                dialog opens so an empty pre-fill (which the backend guardrail
                blocks) is obvious before the manager clicks Save. */}
            <div
              className={`flex items-start gap-3 border p-3 text-sm ${
                willTripEmptyRosterGuard
                  ? "border-amber-500/50 bg-amber-500/10 text-amber-900 dark:text-amber-200"
                  : "border-border bg-muted/40"
              }`}
              data-testid="af-roster-preview"
            >
              {willTripEmptyRosterGuard ? (
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              ) : (
                <Users className="h-4 w-4 text-accent shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                {rosterPreview.count > 0 ? (
                  <>
                    <div className="font-medium">
                      {rosterPreview.count} guest{rosterPreview.count === 1 ? "" : "s"}
                      <span className="text-muted-foreground"> · </span>
                      <span className="text-foreground/85">
                        {rosterPreview.cabins.map((c) => `${c.name}${c.n > 1 ? ` (×${c.n})` : ""}`).join(" · ")}
                      </span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Adjust each guest's cabin below. This roster becomes active on the effective date above.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-medium">Empty roster — nothing will be saved.</div>
                    <div className="text-[11px] mt-0.5 opacity-90">
                      {willTripEmptyRosterGuard
                        ? "Your charter has a populated arrangement. Saving an empty roster is blocked to protect guest login — add guests below, or Cancel this dialog and use the Cancel button on the timeline row instead."
                        : "Add at least one guest to this charter before creating an arrangement."}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div>
              <Label className="overline text-primary/60">Effective from ({tz})</Label>
              <Input
                type="datetime-local"
                value={form.effective_from && form.effective_from.length > 16 ? toLocalInputValue(form.effective_from) : form.effective_from}
                onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                data-testid="af-effective-from"
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">Stored in UTC. All calendars re-render in the yacht's current timezone.</p>
            </div>
            <div>
              <Label className="overline text-primary/60">Notes (optional)</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Owner switched cabins on day 3" data-testid="af-notes" className="mt-2" />
            </div>
            <div>
              <Label className="overline text-primary/60">Roster</Label>
              <div className="mt-2 space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                {(form.guests || []).map((g) => {
                  const isTarget = focusCabinId && g.cabin_id === focusCabinId;
                  return (
                    <div key={g.guest_id} className={`grid grid-cols-2 gap-2 items-center border p-2 transition-colors ${isTarget ? "border-accent bg-accent/5" : "border-border"}`}>
                      <div className="text-sm font-medium flex items-center gap-2">
                        {isTarget && <span className="w-1.5 h-1.5 rounded-full bg-accent" aria-hidden />}
                        {g.name}
                      </div>
                      <Select value={g.cabin_id || ""} onValueChange={(v) => setGuestCabin(g.guest_id, v)}>
                        <SelectTrigger data-testid={`af-cabin-${g.guest_id}`}><SelectValue placeholder="Cabin" /></SelectTrigger>
                        <SelectContent>
                          {cabins.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.id === focusCabinId ? " ★" : ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
                {(form.guests || []).length === 0 && (
                  <div className="text-xs text-muted-foreground">No guests to arrange. Add guests to the charter first.</div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={submit}
                disabled={willTripEmptyRosterGuard}
                className="bg-primary hover:bg-primary/90"
                data-testid="af-save"
                title={willTripEmptyRosterGuard ? "Empty roster — add at least one guest first" : ""}
              >Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
